import React, {
  useMemo, useEffect, useState,
} from 'react';
import { createPortal } from 'react-dom';
import { useSelector, useDispatch, batch } from 'react-redux';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';

import Button from '../../../../../shared/_TBComponents/Button';
import Icon from '../../../../../shared/_TBComponents/Icon';
import Text from '../../../../../shared/_TBComponents/Text';
import {
  fetchUser, fetchAthleteAssignments, fetchArchivedAthletes,
} from '../../../ducks/navigatorActions';
import NavigatorManageTable, {
  AvatarNameCell,
  CalendarCell,
  GroupsCell,
  LastActivityCell,
  OverflowCell,
  nameSort,
  useActionMenu,
  ActionMenuWrapper,
  ActionMenuButton,
  ActionMenuContainer,
  ActionMenuDivider,
  ActionMenuItemButton,
} from './NavigatorManageTable';

const TabsRow = styled.div`
  display: flex;
  margin: 0 16px 16px 16px;
  box-sizing: border-box;
  flex-shrink: 0;
  border-bottom: 1px solid #D6D6D6;
`;

const TabButton = styled.button`
  padding-bottom: 8px;
  margin-right: 16px;
  font-family: "Nunito Sans";
  font-size: 16px;
  font-weight: ${(props) => (props.active ? 700 : 400)};
  color: #1A1A1A;
  border: none;
  border-bottom: 3px solid ${(props) => (props.active ? '#1787FB' : 'transparent')};
  background: none;
  cursor: pointer;
  margin-bottom: -1px;
  transition: color 0.15s, border-color 0.15s;

  &:hover {
    color: #1A1A1A;
  }
`;

const FilterButtonWrapper = styled.div`
  position: relative;
  display: inline-flex;
  flex-shrink: 0;
`;

const FilterBadge = styled.div`
  position: absolute;
  top: -6px;
  right: -6px;
  display: flex;
  width: 16px;
  height: 16px;
  justify-content: center;
  align-items: center;
  background: #000;
  border-radius: 50%;
  color: #FFF;
  font-family: "Nunito Sans";
  font-size: 10px;
  font-weight: 700;
  line-height: 1;
  pointer-events: none;
`;

const ArchiveBanner = styled.div`
  width: 100%;
  box-sizing: border-box;
  background-color: #F8F9FA;
  border: 1px solid #DEE2E6;
  border-radius: 6px;
  padding: 10px 16px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  column-gap: 4px;
`;

const ArchivedActionsWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  @media screen and (max-width: 767px) {
    display: none;
  }
`;

const ArchivedActionsMobileWrapper = styled.div`
  display: none;

  @media screen and (max-width: 767px) {
    display: flex;
  }
`;

const ArchivedTableWidthOverride = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;

  @media screen and (max-width: 767px) {
    [data-column-id="actions"] {
      width: 100px !important;
      min-width: 100px !important;
      flex: none !important;
    }
  }
`;

// Athlete-Specific Cell Components

const ActionDropdownCell = ({ row }) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();

  return (
    <ActionMenuWrapper onClick={(e) => e.stopPropagation()}>
      <ActionMenuButton ref={buttonRef} type='button' onClick={openMenu}>
        <Icon icon='dots' size={16} color='currentColor' />
      </ActionMenuButton>
      {isOpen && createPortal(
        <ActionMenuContainer ref={menuRef} style={menuStyle}>
          <ActionMenuItemButton
            type='button'
            onClick={() => {
              const today = new Date().toISOString().slice(0, 10);
              window.open(`/build?athleteId=${row.original.id}&date=${today}`, '_blank');
              close();
            }}
          >
            <Icon icon='build' size={16} color='#1A1A1A' />
            Build
          </ActionMenuItemButton>
          <ActionMenuItemButton type='button' onClick={() => { row.original.onEdit(row.original); close(); }}>
            <Icon icon='edit-workout' size={16} color='#1A1A1A' />
            Edit
          </ActionMenuItemButton>
          <ActionMenuItemButton type='button' onClick={() => { row.original.onApa(row.original); close(); }}>
            <Icon icon='pr-tracking' size={16} color='#1A1A1A' />
            View Assessment
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' onClick={() => { row.original.onArchive(row.original); close(); }}>
            <Icon icon='archive' size={16} color='#1A1A1A' />
            Archive
          </ActionMenuItemButton>
          <ActionMenuDivider />
          <ActionMenuItemButton type='button' onClick={() => { row.original.onDelete(row.original); close(); }}>
            <Icon icon='delete' size={16} color='#FF0505' />
            Delete
          </ActionMenuItemButton>
        </ActionMenuContainer>,
        document.body,
      )}
    </ActionMenuWrapper>
  );
};

ActionDropdownCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      id: PropTypes.number,
      onApa: PropTypes.func,
      onEdit: PropTypes.func,
      onArchive: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

const ArchivedActionsCell = ({ row }) => {
  const {
    isOpen, menuStyle, menuRef, buttonRef, openMenu, close,
  } = useActionMenu();

  return (
    <>
      <ArchivedActionsWrapper onClick={(e) => e.stopPropagation()}>
        <Button
          name='Restore'
          icon='restore'
          iconColor='#FFF'
          iconSize={13}
          bgColor='#1A1A1A'
          borderColor='#1A1A1A'
          txtColor='#FFF'
          fontSize='14px'
          customBorderRadius='4px'
          styledWrapperProps={{ padding: '8px 16px' }}
          styledTextProps={{ fontWeight: 700 }}
          onClick={() => row.original.onRestore(row.original)}
        />
        <Button
          name='Delete'
          icon='delete'
          iconColor='#FF0505'
          iconSize={13}
          bgColor='#FFF'
          txtColor='#1A1A1A'
          fontSize='14px'
          customBorderRadius='4px'
          styledWrapperProps={{ padding: '8px 16px', border: '2px solid #EEEEEE' }}
          styledTextProps={{ fontWeight: 700 }}
          onClick={() => row.original.onDelete(row.original)}
        />
      </ArchivedActionsWrapper>
      <ArchivedActionsMobileWrapper onClick={(e) => e.stopPropagation()}>
        <ActionMenuWrapper>
          <ActionMenuButton ref={buttonRef} type='button' onClick={openMenu}>
            <Icon icon='dots' size={16} color='currentColor' />
          </ActionMenuButton>
          {isOpen && createPortal(
            <ActionMenuContainer ref={menuRef} style={menuStyle}>
              <ActionMenuItemButton
                type='button'
                onClick={() => { row.original.onRestore(row.original); close(); }}
              >
                <Icon icon='restore' size={16} color='#1A1A1A' />
                Restore
              </ActionMenuItemButton>
              <ActionMenuDivider />
              <ActionMenuItemButton
                type='button'
                onClick={() => { row.original.onDelete(row.original); close(); }}
              >
                <Icon icon='delete' size={16} color='#FF0505' />
                Delete
              </ActionMenuItemButton>
            </ActionMenuContainer>,
            document.body,
          )}
        </ActionMenuWrapper>
      </ArchivedActionsMobileWrapper>
    </>
  );
};

ArchivedActionsCell.propTypes = {
  row: PropTypes.shape({
    original: PropTypes.shape({
      onRestore: PropTypes.func,
      onDelete: PropTypes.func,
    }),
  }).isRequired,
};

// Main Component

const ManageAthletesTable = ({
  onEditAthlete, onDeleteAthlete, onResendActivation, onArchiveAthlete,
  onRestoreAthlete, isArchived, onToggleArchived, archivedUserSlots, onOpenSupport, onBulkAction,
  columnVisibility, onOpenFilter, hasActiveFilters, activeFiltersCount, onVisibleAthletesChange,
  forceLoading,
}) => {
  const dispatch = useDispatch();

  const athletes = useSelector((state) => state.navigator.data.athletes);
  const archivedAthletes = useSelector((state) => state.navigator.data.archivedAthletes);
  const calendars = useSelector((state) => state.navigator.data.calendars);
  const allCalendars = useSelector((state) => state.calendars.data.unparsedCalendars);
  const isLoadingAthletes = useSelector((state) => state.navigator.ui.isLoadingAthletes);
  const isLoadingArchivedAthletes = useSelector(
    (state) => state.navigator.ui.isLoadingArchivedAthletes,
  );
  const accountCode = useSelector((state) => state.auth.data.currentUser?.accountCode);
  const accountsList = useSelector((state) => state.auth.data.currentUser?.accountsList);
  const usernamesEnabled = useSelector(
    (state) => state.auth.data.organizationSettings?.generalSettings?.usernamesEnabled,
  );
  const identifierKey = usernamesEnabled ? 'username' : 'email';
  const orgSettings = useSelector((state) => state.auth.data.organizationSettings);
  const smartabaseEnabled = orgSettings?.smartabaseEnabled;
  const ssoEnabled = orgSettings?.sso?.enabled;
  const ssoLoginName = orgSettings?.sso?.loginName;

  const [searchQuery, setSearchQuery] = useState('');

  const activeSearchKeys = useMemo(
    () => ['first', 'last', 'fullName', identifierKey, 'id', 'externalId', 'ssoId'],
    [identifierKey],
  );
  const archivedSearchKeys = useMemo(
    () => ['first', 'last', 'fullName', identifierKey],
    [identifierKey],
  );

  useEffect(() => {
    if (isArchived && accountCode) {
      dispatch(fetchArchivedAthletes(accountCode));
    }
  }, [isArchived, accountCode, dispatch]);

  const calendarMap = useMemo(
    () => calendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {}),
    [calendars],
  );

  const allCalendarsMap = useMemo(
    () => allCalendars.reduce((acc, cal) => { acc[cal.id] = cal; return acc; }, {}),
    [allCalendars],
  );

  const tableData = useMemo(
    () => athletes.map((athlete) => {
      const calendarId = athlete.calendarAssignment?.calendarId;
      // Falls back to the archived-inclusive map so an archived assignment
      // still shows its real name (tagged) instead of a blank column.
      const cal = calendarMap[calendarId] || allCalendarsMap[calendarId];
      const calendarName = cal?.name || '';
      return {
        ...athlete,
        fullName: `${athlete.first || ''} ${athlete.last || ''}`,
        calendarName,
        calendarAssignments: cal
          ? [{ id: calendarId, name: cal.name, ...(cal.archived && { tag: 'Archived' }) }]
          : [],
        onResendActivation,
        onApa: (athleteRow) => window.open(`/report-card/user/${athleteRow.id}`, '_blank'),
        onEdit: (athleteRow) => {
          batch(() => {
            dispatch(fetchUser(accountCode, athleteRow.id));
            dispatch(fetchAthleteAssignments(accountCode, athleteRow.id));
          });
          onEditAthlete();
        },
        onArchive: onArchiveAthlete,
        onDelete: onDeleteAthlete,
      };
    }),
    [
      athletes, calendarMap, allCalendarsMap, onEditAthlete, onDeleteAthlete,
      onResendActivation, onArchiveAthlete, accountCode, dispatch,
    ],
  );

  const archivedTableData = useMemo(
    () => archivedAthletes.map((athlete) => ({
      ...athlete,
      fullName: `${athlete.first || ''} ${athlete.last || ''}`,
      onRestore: onRestoreAthlete,
      onDelete: onDeleteAthlete,
    })),
    [archivedAthletes, onDeleteAthlete, onRestoreAthlete],
  );

  const columns = useMemo(() => {
    const cols = [
      {
        Header: 'First',
        accessor: 'first',
        sortType: nameSort,
        headerStyle: { width: '240px', padding: '12px 16px 12px 8px' },
        cellStyle: { width: '240px', padding: '6px 16px 6px 8px' },
        Cell: AvatarNameCell,
      },
      {
        Header: 'Last',
        accessor: 'last',
        sortType: nameSort,
        headerStyle: { width: '190px' },
        Cell: OverflowCell,
      },
      {
        Header: usernamesEnabled ? 'Username' : 'Email',
        accessor: usernamesEnabled ? 'username' : 'email',
        headerStyle: { minWidth: '250px', flex: '1' },
        cellStyle: { minWidth: '250px', flex: '1', overflow: 'hidden' },
        Cell: OverflowCell,
      },
    ];

    if (columnVisibility.userId) {
      cols.push({
        Header: 'User ID',
        accessor: 'id',
        disableSortBy: true,
        headerStyle: { width: '100px', textAlign: 'left' },
        cellStyle: { width: '100px', textAlign: 'left' },
        Cell: OverflowCell,
      });
    }
    if (columnVisibility.externalId) {
      cols.push({
        Header: 'External ID',
        accessor: 'externalId',
        disableSortBy: true,
        headerStyle: { width: '130px', textAlign: 'left' },
        cellStyle: { width: '130px', textAlign: 'left' },
        Cell: OverflowCell,
      });
    }
    if (columnVisibility.ssoId && ssoEnabled) {
      cols.push({
        Header: `${ssoLoginName || 'SSO'} ID`,
        accessor: 'ssoId',
        disableSortBy: true,
        headerStyle: { width: '130px', textAlign: 'left' },
        cellStyle: { width: '130px', textAlign: 'left' },
        Cell: OverflowCell,
      });
    }
    if (columnVisibility.smartabaseId && smartabaseEnabled) {
      cols.push({
        Header: 'Smartabase ID',
        accessor: 'smartabaseId',
        disableSortBy: true,
        headerStyle: { width: '150px', textAlign: 'left' },
        cellStyle: { width: '150px', textAlign: 'left' },
        Cell: OverflowCell,
      });
    }

    cols.push(
      {
        Header: 'Groups',
        id: 'groups',
        accessor: (row) => (row.groupAssignments || []).map((g) => g.name).join(' '),
        headerStyle: { width: '250px', textAlign: 'left' },
        cellStyle: { width: '250px', textAlign: 'left' },
        Cell: GroupsCell,
      },
      {
        Header: 'Calendar',
        accessor: 'calendarName',
        headerStyle: { width: '200px', textAlign: 'left' },
        cellStyle: { width: '200px', textAlign: 'left' },
        Cell: CalendarCell,
      },
    );

    if (columnVisibility.joinedDate) {
      cols.push({
        Header: 'Joined Date',
        accessor: 'joinedDate',
        disableSortBy: true,
        headerStyle: { width: '150px', textAlign: 'left' },
        cellStyle: { width: '150px', textAlign: 'left' },
        Cell: OverflowCell,
      });
    }

    cols.push(
      {
        Header: 'Last Activity',
        id: 'lastActivity',
        accessor: (row) => (row.activated === 1 ? row.lastActivity : 'Invited'),
        disableSortBy: true,
        headerStyle: { width: '150px', textAlign: 'left' },
        cellStyle: { width: '150px', textAlign: 'left' },
        Cell: LastActivityCell,
      },
      {
        Header: 'Actions',
        id: 'actions',
        sticky: 'right',
        disableSortBy: true,
        headerStyle: { width: '100px', textAlign: 'center' },
        cellStyle: { width: '100px', textAlign: 'center' },
        Cell: ActionDropdownCell,
      },
    );

    return cols;
  }, [columnVisibility, usernamesEnabled, ssoEnabled, ssoLoginName, smartabaseEnabled]);

  const archivedColumns = useMemo(() => [
    {
      Header: 'User ID',
      accessor: 'userId',
      disableSortBy: true,
      headerStyle: { width: '200px', textAlign: 'left' },
      cellStyle: { width: '200px', textAlign: 'left' },
      Cell: OverflowCell,
    },
    {
      Header: 'First',
      accessor: 'first',
      sortType: nameSort,
      headerStyle: { width: '200px' },
    },
    {
      Header: 'Last',
      accessor: 'last',
      sortType: nameSort,
      headerStyle: { width: '200px' },
    },
    {
      Header: usernamesEnabled ? 'Username' : 'Email',
      accessor: usernamesEnabled ? 'username' : 'email',
      headerStyle: { minWidth: '300px', flex: '1' },
      cellStyle: { minWidth: '300px', flex: '1', overflow: 'hidden' },
    },
    {
      Header: 'Actions',
      id: 'actions',
      sticky: 'right',
      disableSortBy: true,
      headerStyle: { width: '300px', textAlign: 'center' },
      cellStyle: { width: '300px', textAlign: 'center' },
      Cell: ArchivedActionsCell,
    },
  ], [usernamesEnabled]);

  const athleteBulkActions = useMemo(() => {
    const actions = [
      {
        key: 'changeCalendars', label: 'Change Calendars', icon: 'calendar2', iconColor: '#1A1A1A',
      },
      {
        key: 'changeGroups', label: 'Change Groups', icon: 'Users', iconColor: '#1A1A1A',
      },
      {
        key: 'assignPrograms', label: 'Assign Programs', icon: 'templates', iconColor: '#1A1A1A',
      },
      {
        key: 'emailAssessments', label: 'Email Assessments', icon: 'share-ams', iconColor: '#1A1A1A',
      },
    ];
    if (accountsList?.length > 1) {
      actions.push({
        key: 'moveAccount',
        label: 'Move to New Account',
        icon: 'swap-alt',
        iconColor: '#1A1A1A',
        dividerBefore: true,
      });
    }
    actions.push({
      key: 'deleteAthletes',
      label: 'Delete',
      icon: 'delete',
      iconColor: '#FF0505',
      dividerBefore: true,
    });
    return actions;
  }, [accountsList]);

  return (
    <>
      <TabsRow>
        <TabButton
          type='button'
          active={!isArchived}
          onClick={() => onToggleArchived(false)}
        >
          Active Athletes
        </TabButton>
        <TabButton
          type='button'
          active={isArchived}
          onClick={() => onToggleArchived(true)}
        >
          Archived
        </TabButton>
      </TabsRow>

      {isArchived ? (
        <ArchivedTableWidthOverride>
          <NavigatorManageTable
            key='archived'
            data={archivedTableData}
            columns={archivedColumns}
            isLoading={isLoadingArchivedAthletes}
            searchKeys={archivedSearchKeys}
            searchPlaceholder='Search for an athlete'
            emptyMessage='No archived athletes.'
            searchEmptyMessage='No archived athletes match your search.'
            entityLabel='athlete'
            defaultSortBy={[{ id: 'last', desc: false }]}
            onFilteredDataChange={onVisibleAthletesChange}
            searchQuery={searchQuery}
            onSearchQueryChange={setSearchQuery}
            bannerPosition='inline'
            bannerContent={archivedUserSlots != null ? (
              <ArchiveBanner>
                <Text styledTextProps={{ fontSize: '13px', color: '#444444' }}>
                  You are able to archive up to
                </Text>
                <Text styledTextProps={{ fontSize: '13px', fontWeight: 700, color: '#444444' }}>
                  {archivedUserSlots}
                </Text>
                <Text styledTextProps={{ fontSize: '13px', color: '#444444' }}>
                  athletes as part of your subscription. Please contact
                </Text>
                <Button
                  name='Support'
                  bgColor='transparent'
                  noBorder
                  txtColor='#77A7FF'
                  styledWrapperProps={{ padding: '0' }}
                  styledTextProps={{ fontSize: '13px', letterSpacing: 'normal', fontWeight: 600 }}
                  onClick={onOpenSupport}
                />
                <Text styledTextProps={{ fontSize: '13px', color: '#444444' }}>
                  to increase your limit.
                </Text>
              </ArchiveBanner>
            ) : null}
            virtualizeRows
          />
        </ArchivedTableWidthOverride>
      ) : (
        <NavigatorManageTable
          key='active'
          // Force an empty data set during a deep-link filter, else stale rows stay visible.
          data={forceLoading ? [] : tableData}
          columns={columns}
          isLoading={isLoadingAthletes || forceLoading}
          searchKeys={activeSearchKeys}
          searchPlaceholder='Search for an athlete'
          emptyMessage='No athletes found.'
          searchEmptyMessage='No athletes match your search.'
          bulkActions={athleteBulkActions}
          onBulkAction={onBulkAction}
          entityLabel='athlete'
          defaultSortBy={[{ id: 'last', desc: false }]}
          onFilteredDataChange={onVisibleAthletesChange}
          searchQuery={searchQuery}
          onSearchQueryChange={setSearchQuery}
          extraActionContent={(
            <FilterButtonWrapper>
              <Button
                name='Filter'
                icon='filter2'
                iconColor='#1A1A1A'
                txtColor='#1A1A1A'
                bgColor='var(--theme-bg-surface, #FFF)'
                borderColor='#D6D6D6'
                customBorderRadius='4px'
                styledTextProps={{ fontSize: '14px', fontWeight: 700, letterSpacing: '0.3px' }}
                styledWrapperProps={{
                  padding: '8.5px 18px',
                  boxShadow: '0 1px 3px 0 rgba(48, 49, 51, 0.10), 0 0 1px 0 rgba(48, 49, 51, 0.05)',
                }}
                onClick={onOpenFilter}
              />
              {hasActiveFilters && <FilterBadge>{activeFiltersCount}</FilterBadge>}
            </FilterButtonWrapper>
          )}
          virtualizeRows
        />
      )}
    </>
  );
};

ManageAthletesTable.propTypes = {
  onEditAthlete: PropTypes.func.isRequired,
  onDeleteAthlete: PropTypes.func.isRequired,
  onResendActivation: PropTypes.func.isRequired,
  onArchiveAthlete: PropTypes.func.isRequired,
  onRestoreAthlete: PropTypes.func.isRequired,
  onToggleArchived: PropTypes.func.isRequired,
  onOpenSupport: PropTypes.func.isRequired,
  onBulkAction: PropTypes.func.isRequired,
  isArchived: PropTypes.bool,
  archivedUserSlots: PropTypes.number,
  columnVisibility: PropTypes.shape({
    userId: PropTypes.bool,
    externalId: PropTypes.bool,
    ssoId: PropTypes.bool,
    smartabaseId: PropTypes.bool,
    joinedDate: PropTypes.bool,
  }),
  onOpenFilter: PropTypes.func.isRequired,
  hasActiveFilters: PropTypes.bool,
  activeFiltersCount: PropTypes.number,
  onVisibleAthletesChange: PropTypes.func,
  forceLoading: PropTypes.bool,
};

ManageAthletesTable.defaultProps = {
  isArchived: false,
  archivedUserSlots: null,
  columnVisibility: {},
  hasActiveFilters: false,
  activeFiltersCount: 0,
  onVisibleAthletesChange: () => {},
  forceLoading: false,
};

export default ManageAthletesTable;
